import type { LpStage, LpStamp } from "../lib/domain";
import type { LpStageListener } from "./trace";

const PREVIOUS: Record<LpStage, LpStage | null> = {
  arrived: null,
  calculated: "arrived",
  main: "calculated",
  painted: "main",
};

/** Server-clock delta when both ends have one, else client-clock delta marked `~`. */
const delta = (to: LpStamp, from: LpStamp | { server: number }) =>
  to.server !== null && from.server !== null
    ? `+${Math.round(to.server - from.server)}ms`
    : `+${Math.round(to.client - ("client" in from ? from.client : from.server))}ms~`;

/**
 * Mirrors the trace spans on the console, one line per stage as it happens. `~` marks a delta
 * measured on the client clock because an end had not synced; the total from the market timestamp
 * is then unknown. Meant for the worker debug panel's "Log traces".
 */
export const createLivePriceConsoleLogger = (opts: {
  enabled: () => boolean;
  out?: (line: string) => void;
}): LpStageListener => {
  const out = opts.out ?? ((line) => console.log(line));
  return (trace, stage) => {
    if (!opts.enabled()) return;
    const stamp = trace.ts[stage];
    if (!stamp) return;
    const previous = PREVIOUS[stage];
    const from = (previous && trace.ts[previous]) || { server: trace.marketTs };
    const total = stamp.server === null ? "total ?" : `total ${Math.round(stamp.server - trace.marketTs)}ms`;
    const extra =
      stage === "arrived"
        ? `trace ${trace.backendTraceId.slice(0, 8)} rows ${trace.rowCount}`
        : stage === "calculated" && trace.version !== undefined
          ? `v${trace.version}`
          : "";
    out(`${`lp.${stage}`.padEnd(15)}${delta(stamp, from).padEnd(8)}${total.padEnd(14)}${extra}`.trimEnd());
  };
};
